import type { Event, ScheduleConfiguration, EventForm } from '../types';

// Re-export types for components
export type { EventForm } from '../types';
import { mockApi, type CreateEventRequest } from './mockApi';

// Room type definition
export interface Room {
  id: number;
  eventId: number;
  name: string;
  description?: string;
  capacity: number;
  location?: string;
  equipment: string[];
  isActive: boolean;
  virtualLink?: string;
  virtualRoomId?: string;
  createdAt: string;
}

// Use mock API for development when backend is not available
const USE_MOCK_API = !import.meta.env.VITE_API_BASE_URL;

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Error classes for better error handling
export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export class ValidationError extends ApiError {
  errors: Record<string, string[]>;

  constructor(message: string, errors: Record<string, string[]>) {
    super(message, 400, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
    this.errors = errors;
  }
}

// Generic API client
class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add authentication header if available
    const token = localStorage.getItem('auth_token') || localStorage.getItem('authToken');
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 422 && errorData.errors) {
          throw new ValidationError(
            errorData.message || 'Validation failed',
            errorData.errors
          );
        }
        
        throw new ApiError(
          errorData.message || `HTTP error! status: ${response.status}`,
          response.status,
          errorData.code
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      // Network or other errors
      throw new ApiError(
        'Network error occurred. Please check your connection.',
        0
      );
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

const apiClient = new ApiClient();

// Event API functions
export const eventApi = USE_MOCK_API ? {
  // Create a new event
  async create(eventData: any): Promise<Event> {
    // Convert the data format for mock API
    const createData: CreateEventRequest = {
      title: eventData.title,
      description: eventData.description,
      type: eventData.type,
      startDate: eventData.startDate.toISOString(),
      endDate: eventData.endDate.toISOString(),
      location: eventData.location,
      onlineLink: eventData.onlineLink,
      maxParticipants: eventData.maxParticipants,
      isActive: eventData.isActive,
      reviewDuration: eventData.reviewDuration,
      breakBetweenReviews: eventData.breakBetweenReviews,
      requiresApproval: eventData.requiresApproval,
      isPublic: eventData.isPublic,
      registrationDeadline: eventData.registrationDeadline?.toISOString(),
      categories: eventData.categories,
      tags: eventData.tags,
      expectedOutcomes: eventData.expectedOutcomes,
      requirements: eventData.requirements,
      scheduleConfig: eventData.scheduleConfig
    };
    return mockApi.events.create(createData);
  },

  // Get all events (with optional filters)
  async getAll(filters?: {
    isActive?: boolean;
    type?: Event['type'];
    isPublic?: boolean;
    page?: number;
    limit?: number;
  }): Promise<{
    events: Event[];
    total: number;
    page: number;
    limit: number;
  }> {
    return mockApi.events.getAll(filters);
  },

  // Get a specific event by ID
  async getById(id: string): Promise<Event> {
    const result = await mockApi.events.getAll();
    const event = result.events.find(e => e.id === id);
    if (!event) {
      throw new Error('Event not found');
    }
    return event;
  },

  // Update an existing event
  async update(id: string, eventData: Partial<Event>): Promise<Event> {
    // For mock API, we'll just return the existing event with updated data
    const existing = await this.getById(id);
    return { ...existing, ...eventData, updatedAt: new Date() };
  },

  // Delete an event
  async delete(id: string): Promise<void> {
    // Mock implementation - would actually remove from mockEvents
    console.log('Mock: Event deleted:', id);
  },

  // Duplicate an event
  async duplicate(id: string): Promise<Event> {
    const existing = await this.getById(id);
    const duplicated = {
      ...existing,
      id: (Date.now()).toString(),
      title: `${existing.title} (Copy)`,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    return duplicated;
  }
} : {
  // Create a new event
  async create(eventData: Omit<Event, 'id' | 'createdAt' | 'updatedAt' | 'currentParticipants'>): Promise<Event> {
    return apiClient.post<Event>('/api/events', eventData);
  },

  // Get all events (with optional filters)
  async getAll(filters?: {
    isActive?: boolean;
    type?: Event['type'];
    isPublic?: boolean;
    page?: number;
    limit?: number;
  }): Promise<{
    events: Event[];
    total: number;
    page: number;
    limit: number;
  }> {
    const searchParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.set(key, String(value));
        }
      });
    }

    const query = searchParams.toString();
    return apiClient.get<{
      events: Event[];
      total: number;
      page: number;
      limit: number;
    }>(`/api/events${query ? `?${query}` : ''}`);
  },

  // Get a specific event by ID
  async getById(id: string): Promise<Event> {
    return apiClient.get<Event>(`/api/events/${id}`);
  },

  // Update event status
  async updateStatus(id: string, isActive: boolean): Promise<Event> {
    const existing = await this.getById(id);
    return { ...existing, isActive, updatedAt: new Date() };
  },

  // Get event statistics
  async getStats(_id: string): Promise<{
    totalRegistrations: number;
    approvedRegistrations: number;
    pendingRegistrations: number;
    totalSlots: number;
    claimedSlots: number;
    completedReviews: number;
  }> {
    // Mock statistics
    return {
      totalRegistrations: 25,
      approvedRegistrations: 20,
      pendingRegistrations: 5,
      totalSlots: 48,
      claimedSlots: 35,
      completedReviews: 12
    };
  }
};

// Schedule Configuration API functions
export const scheduleApi = {
  // Create or update schedule configuration for an event
  async upsert(eventId: string, config: Omit<ScheduleConfiguration, 'id' | 'eventId'>): Promise<ScheduleConfiguration> {
    return apiClient.put<ScheduleConfiguration>(`/api/events/${eventId}/schedule`, config);
  },

  // Get schedule configuration for an event
  async get(eventId: string): Promise<ScheduleConfiguration> {
    return apiClient.get<ScheduleConfiguration>(`/api/events/${eventId}/schedule`);
  },

  // Generate time slots based on configuration
  async generateSlots(eventId: string): Promise<{
    totalSlots: number;
    slotsPerDay: Record<string, number>;
  }> {
    return apiClient.post(`/api/events/${eventId}/schedule/generate`);
  },
};

// Rooms API functions
export const roomsApi = {
  // Create a new room for an event
  async create(eventId: string, roomData: Omit<Room, 'id' | 'eventId' | 'createdAt'>): Promise<Room> {
    return apiClient.post<Room>(`/api/events/${eventId}/rooms`, roomData);
  },

  // Get all rooms for an event
  async getByEventId(eventId: string): Promise<{
    rooms: Room[];
    total: number;
  }> {
    return apiClient.get<{
      rooms: Room[];
      total: number;
    }>(`/api/events/${eventId}/rooms`);
  },

  // Update an existing room
  async update(roomId: string, roomData: Partial<Omit<Room, 'id' | 'eventId' | 'createdAt'>>): Promise<Room> {
    return apiClient.put<Room>(`/api/rooms/${roomId}`, roomData);
  },

  // Delete a room
  async delete(roomId: string): Promise<void> {
    return apiClient.delete<void>(`/api/rooms/${roomId}`);
  },
};

// Authentication API functions
export const authApi = {
  // Login
  async login(email: string, password: string): Promise<{
    user: any;
    token: string;
    expiresAt: string;
  }> {
    return apiClient.post('/api/auth/login', { email, password });
  },

  // Logout
  async logout(): Promise<void> {
    return apiClient.post('/api/auth/logout');
  },

  // Refresh token
  async refresh(): Promise<{
    token: string;
    expiresAt: string;
  }> {
    return apiClient.post('/api/auth/refresh');
  },

  // Get current user
  async me(): Promise<any> {
    return apiClient.get('/api/auth/user');
  },
};

// Export the API client for custom requests
export { apiClient };

// Helper function to handle API errors in components
export const handleApiError = (error: unknown): string => {
  if (error instanceof ValidationError) {
    const firstError = Object.values(error.errors)[0];
    return firstError ? firstError[0] : 'Validation failed';
  }
  
  if (error instanceof ApiError) {
    return error.message;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
};

// Helper function to check if user is authenticated
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('auth_token');
  return !!token;
};

// Helper function to get current user ID from token (if using JWT)
export const getCurrentUserId = (): string | null => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('authToken');
  if (!token) return null;
  
  // For mock authentication, we'll return a default user ID
  if (token === 'mock-token') {
    return '1'; // Mock user ID
  }
  
  try {
    // If using real JWT, decode the token to get user ID
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub || payload.userId || null;
  } catch {
    return null;
  }
};

// Forms API functions
export const formsApi = {
  // Create a new form for an event
  async create(eventId: string, formData: Omit<EventForm, 'id' | 'eventId' | 'createdAt'>): Promise<EventForm> {
    return apiClient.post<EventForm>(`/api/events/${eventId}/forms`, formData);
  },

  // Get all forms for an event
  async getByEventId(eventId: string): Promise<{
    forms: EventForm[];
    total: number;
  }> {
    return apiClient.get<{
      forms: EventForm[];
      total: number;
    }>(`/api/events/${eventId}/forms`);
  },

  // Update an existing form
  async update(formId: string, formData: Partial<Omit<EventForm, 'id' | 'eventId' | 'createdAt'>>): Promise<EventForm> {
    return apiClient.put<EventForm>(`/api/forms/${formId}`, formData);
  },

  // Delete a form
  async delete(formId: string): Promise<void> {
    return apiClient.delete<void>(`/api/forms/${formId}`);
  },

  // Get a specific form by ID
  async getById(formId: string): Promise<EventForm> {
    return apiClient.get<EventForm>(`/api/forms/${formId}`);
  },
};